import React, { FC, Fragment } from 'react';

import InfiniteScroll from 'react-infinite-scroller';

import { Row, Col } from '@crehana/ui';
import { SearchContentProps } from '@crehana/web/dist/MainMenu/components/Search/types';
import { useMainMenuContext } from '@crehana/web/dist/MainMenu/hooks';
import NavbarContainer from '@crehana/web/dist/MainMenu/ui/NavbarContainer';
import { useProductsContainer } from '@crehana/web/dist/ReactContexts/Products';

import { SearchCourse } from '../types';
import {
  PHONE_LEAD_CARD_POSITION,
  COURSES_PER_ROW,
  CUSTOM_GRID_GUTTER,
} from '../constants';

import CourseListSkeleton from '../ui/CourseListSkeleton';
import SearchCourseCard from '../components/SearchCourseCard';
import PhoneLeadCard from '../components/PhoneLeadCard';

type SearchResultsProps = Pick<
  SearchContentProps,
  | 'textColorClassName'
  | 'isDark'
  | 'shouldRenderPhoneLeadCard'
  | 'hidePhoneLeadCard'
> & {
  results: SearchCourse[];
  hasMore: boolean;
  source?: string;
  sourceDetail?: string;
  onFetchMore: () => void;
};

const SearchResults: FC<SearchResultsProps> = ({
  textColorClassName,
  isDark,
  shouldRenderPhoneLeadCard,
  hidePhoneLeadCard,
  results,
  hasMore,
  source,
  sourceDetail,
  onFetchMore,
}) => {
  const { currentBreakpointKey } = useMainMenuContext();

  const {
    shoppingCartState: { loadingCart, courseIdsInCart },
    addCourseToCart,
    removeCourseFromCart,
  } = useProductsContainer();

  const renderResults = (startIndex: number, endIndex: number) => (
    <Fragment>
      {results.slice(startIndex, endIndex).map(course => (
        <Col
          key={`search-landing-course-${course.originalId}`}
          xs={12}
          sm={4}
          md={3}
          gutter={CUSTOM_GRID_GUTTER}
        >
          <SearchCourseCard
            className={textColorClassName}
            isDark={isDark}
            loadingCart={loadingCart}
            source={source}
            sourceDetail={sourceDetail}
            isInShoppingCart={
              course.originalId
                ? courseIdsInCart.includes(Number(course.originalId))
                : false
            }
            addCourseToCart={addCourseToCart}
            removeCourseFromCart={removeCourseFromCart}
            {...course}
          />
        </Col>
      ))}
    </Fragment>
  );

  const renderContent = () => {
    if (shouldRenderPhoneLeadCard) {
      const hasMoreCourses = results.length > PHONE_LEAD_CARD_POSITION + 1;

      return (
        <Row gutter={CUSTOM_GRID_GUTTER}>
          {renderResults(0, PHONE_LEAD_CARD_POSITION)}
          <Col xs={12} sm={4} md={3}>
            <PhoneLeadCard
              isDark={isDark}
              hidePhoneLeadCard={hidePhoneLeadCard}
            />
          </Col>
          {hasMoreCourses &&
            renderResults(PHONE_LEAD_CARD_POSITION, results.length - 1)}
        </Row>
      );
    }
    return (
      <Row gutter={CUSTOM_GRID_GUTTER}>{renderResults(0, results.length)}</Row>
    );
  };

  return (
    <NavbarContainer className="flex flex-col">
      <InfiniteScroll
        loadMore={onFetchMore}
        hasMore={hasMore}
        loader={
          <CourseListSkeleton
            key="search-infinite-scroll-skeleton"
            isDark={isDark}
            count={COURSES_PER_ROW[currentBreakpointKey]}
          />
        }
        initialLoad={false}
        threshold={820}
        useWindow
      >
        {renderContent()}
      </InfiniteScroll>
    </NavbarContainer>
  );
};

export default SearchResults;
